import { useEffect, useRef } from 'react'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { trpc } from '@/lib/trpc'
import { useRunbookExecutionStream } from '@/hooks/useRunbookExecutionStream'

/**
 * Delays (ms) after a run reaches a terminal status at which to refetch, to
 * catch declared outputs + artifacts the finalize seam writes around that
 * moment. Two catches cover a same-tick write and a slightly-late one.
 */
const SETTLE_REFETCH_DELAYS_MS = [1500, 4000] as const

export type RunbookRunData = NonNullable<
  ReturnType<typeof trpc.runbook.getRun.queryOptions>['queryFn']
> extends (...args: infer _A) => infer R
  ? Awaited<R>
  : never

interface UseRunbookRunOptions {
  executionId: string | null | undefined
  /** Disable both query and stream (e.g. wrong template type). */
  enabled?: boolean
  /**
   * Poll interval in ms used as a fallback only while SSE is not
   * `open` (initial connect, error, reconnect). Set to `false` to
   * disable polling entirely. Default: 2000ms.
   */
  fallbackPollMs?: number | false
  /**
   * Predicate for whether the run is still progressing — controls
   * whether the fallback poll fires. Default: `status === 'running'`.
   */
  isActive?: (status: string | undefined) => boolean
}

/**
 * One-stop hook for reading a runbook execution. Bundles:
 *   1. `runbook.getRun` tRPC query (cached, shared across consumers).
 *   2. SSE subscription that invalidates on every step transition.
 *   3. Polling fallback that only fires while SSE is not connected.
 *
 * Use this anywhere you'd otherwise call
 * `useQuery(trpc.runbook.getRun.queryOptions(...))`. The returned
 * value is a standard `UseQueryResult` so existing call sites only
 * change at the hook boundary.
 */
export function useRunbookRun({
  executionId,
  enabled = true,
  fallbackPollMs = 2000,
  isActive = (status) => status === 'running',
}: UseRunbookRunOptions): UseQueryResult<RunbookRunData> {
  const id = executionId ?? ''
  const effectiveEnabled = enabled && !!executionId

  const streamStatus = useRunbookExecutionStream({
    executionId: effectiveEnabled ? id : null,
  })

  const query = useQuery({
    ...trpc.runbook.getRun.queryOptions({ id }),
    enabled: effectiveEnabled,
    refetchInterval: (q) => {
      if (fallbackPollMs === false) return false
      const status = (q.state.data as { status?: string } | undefined)?.status
      if (!isActive(status)) return false
      return streamStatus === 'open' ? false : fallbackPollMs
    },
  }) as UseQueryResult<RunbookRunData>

  // Settle refetch on the active → terminal transition. Polling stops the tick
  // it first sees a terminal status, but the run's declared outputs + artifacts
  // are written by the finalize seam around that same moment — so that last poll
  // can miss them and the panel shows an empty "Run Output" until a manual
  // reopen. Fire a couple of delayed refetches to pull the final row.
  const { refetch } = query
  const status = (query.data as { status?: string } | undefined)?.status
  const transitionRef = useRef<{
    executionId: string | null
    active: boolean
  }>({ executionId: null, active: false })
  useEffect(() => {
    const currentExecutionId =
      effectiveEnabled && executionId ? executionId : null
    const active = currentExecutionId ? isActive(status) : false
    const previous = transitionRef.current
    transitionRef.current = { executionId: currentExecutionId, active }

    if (
      !currentExecutionId ||
      !status ||
      previous.executionId !== currentExecutionId ||
      !previous.active ||
      active
    ) {
      return
    }

    const timers = SETTLE_REFETCH_DELAYS_MS.map((ms) =>
      setTimeout(() => void refetch(), ms)
    )
    return () => timers.forEach(clearTimeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionId, effectiveEnabled, status])

  return query
}
